import React from 'react';
import { View } from 'react-native';

import { Text, Button } from '@/components/ui';

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

interface ErrorBoundaryProps {
	children: React.ReactNode;
	fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error('ErrorBoundary caught an error:', error, errorInfo);

		// Log additional context for debugging crashes
		console.error('Error details:', {
			message: error.message,
			stack: error.stack,
			componentStack: errorInfo.componentStack,
			timestamp: new Date().toISOString(),
		});
	}

	resetError = () => {
		this.setState({ hasError: false, error: undefined });
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				const FallbackComponent = this.props.fallback;
				return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
			}

			return (
				<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
					<Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 10, textAlign: 'center' }}>
						Something went wrong
					</Text>
					<Text style={{ fontSize: 14, color: '#666', marginBottom: 20, textAlign: 'center' }}>
						{this.state.error?.message || 'An unexpected error occurred'}
					</Text>
					<Button mode="contained" onPress={this.resetError}>
						<Text>Try Again</Text>
					</Button>
				</View>
			);
		}

		return this.props.children;
	}
}

export const DefaultErrorFallback: React.FC<{ error?: Error; resetError: () => void }> = ({ error, resetError }) => (
	<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
		<Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 10, textAlign: 'center' }}>
			Unable to load content
		</Text>
		<Text style={{ fontSize: 14, color: '#666', marginBottom: 10, textAlign: 'center' }}>
			There was a problem loading this screen. This might be due to a network issue or server error.
		</Text>
		{error?.message ? (
			<Text
				selectable
				style={{ fontSize: 12, color: '#b00020', marginBottom: 20, textAlign: 'center', fontFamily: 'monospace' }}
			>
				{error.message}
			</Text>
		) : null}
		<Button mode="contained" onPress={resetError}>
			<Text>Retry</Text>
		</Button>
	</View>
);
